import { Button, Dropdown, Tooltip, Tree } from 'antd';
import './MapLayersButton.less';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import {
  selectMapTutorial,
} from '../../../store/slice/mapSlice';
import { ComponentType, useCallback, useMemo, useState } from 'react';
import Icon from '@ant-design/icons';
import { MapLayer } from '../../../models/Geometries';
import { selectAllLayers, setLayersList } from '../../../store/slice/mapLayersSlice';
import { setAOIVisible } from '../../../store/slice/mapAOISlice';

interface Props {
  tooltip: string;
  onClick?: () => any;
  icon: ComponentType;
  active?: boolean;
}

type LayerGroup = {
  title: string;
  key?: string; //key para el componente de arbol desplegable
  children: {
    title: string;
    key?: string;
    layer: MapLayer;
  }[];
  checkable?: boolean;
  isAOI?: boolean;
}

function MapLayersButton({ tooltip, onClick, icon, active }: Props) {
  const mapTutorial = useAppSelector(selectMapTutorial);
  const [hover, setHover] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const layers = useAppSelector(selectAllLayers);
  const aoi = useAppSelector(state => state.mapAOI.aoi);
  const aoiVisible = useAppSelector(state => state.mapAOI.aoiVisible);

  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  const [aoiCheckboxKey, setAoiCheckboxKey] = useState<string | undefined>();
  const dispatch = useAppDispatch();

  const tree = useMemo(() => {
    const groups: LayerGroup[] = [];

    layers.forEach(layer => {
      /*
      Cada layer tiene un type.
      Por cada type que existe ('Catastro Minero', 'Productos Satelitales', etc)
      busco si ya existe un grupo. Si existe, agrego esa layer al array de children.
      Si no existe, creo el grupo.
       */
      const existingGroup = groups.find(group => group.title === layer.type);
      if(existingGroup){
        existingGroup.children.push({
          title: layer.title,
          layer: layer,
        });
      } else {
        groups.push({
          title: layer.type,
          children: [{
            title: layer.title,
            layer: layer,
          }]
        })
      }
    })

    //Ordeno los grupos en orden alfabetico
    groups.sort((a,b) => a.title.localeCompare(b.title));

    groups.forEach((firstLevel, i) => {
      firstLevel.key = '0-' + i
      firstLevel.children?.forEach((secondLevel: any, j: number) => {
        secondLevel.key = '0-'+i+'-'+j
      })
    })

    if(aoi){
      const aoiKey = '0-' + groups.length;
      groups.push({
        title: 'Área de interés',
        isAOI: true,
        key: aoiKey,
        children: aoi.layers.map((layer, i) => ({
          title: layer.title,
          layer: layer,
          key: aoiKey + '-' + i,
          checkable: false,
        }))
      })
      setAoiCheckboxKey(aoiKey)
    }

    return groups;
  }, [layers, aoi, aoiVisible]);

  const onCheck = useCallback(
    (checkedKeysValue: any[], info: any) => {
      const layersList: MapLayer[] = [];
      info.checkedNodes.forEach((node: any) => {
        if (node.layer) {
          layersList.push(node.layer);
        }
      });
      dispatch(setAOIVisible(info.checkedNodes.some((node: any) => node.isAOI)))
      dispatch(setLayersList(layersList));
      setCheckedKeys(checkedKeysValue);
    },
    [dispatch],
  );

  return (
    <Dropdown
      visible={visible}
      onVisibleChange={setVisible}
      trigger={['click']}
      overlay={() => (
        <div className={'at-map-layers-button__container'}>
          <Tree
            checkable
            selectable={false}
            onCheck={onCheck as any}
            checkedKeys={[...checkedKeys, ...(aoiVisible ? [aoiCheckboxKey] : [])]}
            treeData={tree}
          />
        </div>
      )}
    >
      <Tooltip
        title={tooltip}
        visible={mapTutorial ? true : hover}
        trigger={'hover'}
        placement={'left'}
      >
        <Button
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className={'at-map-button__button ' + (active || visible ? 'active' : '')}
          size='large'
          shape='round'
          onClick={() => (mapTutorial ? null : onClick?.())}
          type={'ghost'}
          icon={<Icon component={icon} />}
        />
      </Tooltip>
    </Dropdown>
  );
}

export default MapLayersButton;
